import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import {
    Avatar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    Menu,
    MenuItem,
    Stack,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import {
    useCreateRecommendationCommentMutation,
    useDeleteRecommendationCommentMutation,
    useMarkRecommendationCommentsAsReadMutation,
    useRecommendationComments,
    useRecommendationCommentsPermissions,
    useUpdateRecommendationCommentMutation,
} from '/@/hooks/recommendation';
import { useValidatedType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { RecommendationComment } from '/@/types/recommendation';
import { formatDate, getUserTypes } from '/@/utils';
import { CHIP_COLORS } from '../chip-label';

interface RecommendationCommentsDrawerProps {
    open: boolean;
    setCommentsDrawerOpen: (open: boolean) => void;
    recommendationId: number;
}

const RecommendationCommentItem = ({
    comment,
    setAnchorEl,
    setSelectedComment,
    setEditingComment,
    setNewMessage,
    editingComment,
    newMessage,
    handleSend,
    updateComment,
    canSendMessages,
}: {
    comment: RecommendationComment;
    setAnchorEl: (anchorEl: HTMLElement | null) => void;
    setSelectedComment: (comment: RecommendationComment | null) => void;
    setEditingComment: (comment: RecommendationComment | null) => void;
    setNewMessage: (message: string) => void;
    editingComment: RecommendationComment | null;
    newMessage: string;
    handleSend: () => void;
    updateComment: ReturnType<typeof useUpdateRecommendationCommentMutation>;
    canSendMessages: boolean;
}) => {
    const userTypes = getUserTypes();
    const ownUserNodeQuery = useOwnUserNode();
    const createdByUserNodeQuery = useGetUserNode(comment?.createdBy ?? 0);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, comment: RecommendationComment) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedComment(comment);
    };

    const handleCancelEdit = () => {
        setEditingComment(null);
        setNewMessage('');
    };

    const isOwnComment = comment.createdBy === ownUserNodeQuery.data?.id;

    const isEdited = comment.created !== comment.modified;

    if (!createdByUserNodeQuery.data) return null;

    return (
        <ListItem
            key={comment.id}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 0,
                flexDirection: isOwnComment ? 'row-reverse' : 'row',
            }}
            width='100%'
        >
            {/* Avatar */}
            <Avatar
                sx={{
                    background: isOwnComment
                        ? '#006F9B'
                        : CHIP_COLORS[createdByUserNodeQuery.data?.type as keyof typeof CHIP_COLORS]?.background,
                    position: 'relative',
                    overflow: 'visible',
                }}
            >
                <Typography variant='subtitle2' fontWeight='bold' color={isOwnComment ? 'white' : 'primary'}>
                    {createdByUserNodeQuery.data?.name
                        ?.split(' ')
                        .slice(0, 2)
                        .map((name) => name.charAt(0))
                        .join('')}
                </Typography>
            </Avatar>

            {/* Comment Bubble */}
            <Stack
                spacing={1}
                p={2}
                sx={{
                    bgcolor: isOwnComment ? '#E7F8FF' : '#F5F5F5',
                    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    position: 'relative',
                }}
                width='100%'
            >
                {/* Name and Menu */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 1.5,
                    }}
                >
                    {isOwnComment && canSendMessages && !editingComment && (
                        <IconButton
                            size='small'
                            onClick={(e: React.MouseEvent<HTMLElement>) => handleMenuOpen(e, comment)}
                            sx={{ p: 1 }}
                            color='primary'
                        >
                            <MoreVertIcon fontSize='small' />
                        </IconButton>
                    )}
                </Box>
                <Stack spacing={1}>
                    {createdByUserNodeQuery.data?.type && (
                        <Typography variant='overline' fontWeight='bold' color='primary.mui.disabled'>
                            {userTypes[createdByUserNodeQuery.data?.type as keyof typeof userTypes]}
                        </Typography>
                    )}

                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {createdByUserNodeQuery.data?.name}
                    </Typography>

                    {/* Message or Edit Field */}
                    {editingComment && editingComment.id === comment.id ? (
                        <Box sx={{ mb: 2, background: 'white' }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                variant='outlined'
                                size='small'
                                color='primary'
                                InputProps={{
                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        if (e.key === 'Escape') {
                                            handleCancelEdit();
                                        } else if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    },
                                    endAdornment: (
                                        <Stack spacing={0.5} direction='row' alignItems='center'>
                                            <IconButton onClick={handleCancelEdit} size='small'>
                                                <CloseIcon fontSize='small' />
                                            </IconButton>
                                            <IconButton
                                                onClick={handleSend}
                                                disabled={!newMessage.trim() || updateComment.isMutating}
                                                size='small'
                                            >
                                                <SendIcon fontSize='small' />
                                            </IconButton>
                                        </Stack>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        top: '-10px',
                                    },
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography variant='body2' color='primary' mb={2} whiteSpace='pre-wrap'>
                            {comment.message}
                        </Typography>
                    )}

                    {/* Timestamp */}
                    <Stack direction='row' justifyContent='flex-end' spacing={0.5} mt={2}>
                        <Typography variant='caption' sx={{ color: '#006F9B', fontStyle: 'italic' }}>
                            {isEdited ? _('Editado') : ''}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#006F9B' }}>
                            {formatDate(new Date(comment.modified))}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#006F9B' }}>
                            {new Date(comment.modified).toLocaleTimeString(undefined, {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                            })}
                            h
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
        </ListItem>
    );
};

const RecommendationCommentsDrawer = ({
    open,
    recommendationId,
    setCommentsDrawerOpen,
}: RecommendationCommentsDrawerProps) => {
    const [newMessage, setNewMessage] = useState('');
    const [editingComment, setEditingComment] = useState<RecommendationComment | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedComment, setSelectedComment] = useState<RecommendationComment | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const recommendationQuery = useRecommendationById(recommendationId);
    const { canReadComments, canSendMessages } = useRecommendationCommentsPermissions(
        undefined,
        recommendationQuery.data?.userNode
    );

    const { data: commentsData, isLoading } = useRecommendationComments(
        { recommendation: recommendationId, ordering: 'created' },
        { enabled: open && !!recommendationId && canReadComments }
    );

    const createComment = useCreateRecommendationCommentMutation();
    const updateComment = useUpdateRecommendationCommentMutation();
    const deleteComment = useDeleteRecommendationCommentMutation();
    const markAsRead = useMarkRecommendationCommentsAsReadMutation();

    const comments = commentsData?.results || [];

    // Mark comments as read when drawer opens
    useEffect(() => {
        if (open && recommendationId) {
            // Mark all comments as read
            markAsRead.trigger({ recommendation: recommendationId }).catch(() => {
                // Silently fail if marking as read fails
            });
        }
    }, [open, recommendationId, markAsRead.trigger]); // Only run when drawer opens

    useEffect(() => {
        if (open && comments.length > 0) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [open, comments.length]);

    const validatedUserType = useValidatedType();

    const handleSend = async () => {
        if (!newMessage.trim() || !recommendationId || !canSendMessages) return;

        if (editingComment) {
            // Update existing comment
            await updateComment.trigger({
                id: editingComment.id,
                message: newMessage.trim(),
            });
            setEditingComment(null);
        } else {
            // Create new comment
            await createComment.trigger({
                recommendation: recommendationId,
                message: newMessage.trim(),
            });
            mpTrackEvent('Recommendation comment created', {
                recommendation_id: recommendationId,
                user_type: validatedUserType,
            });
        }
        setNewMessage('');
    };

    const handleEdit = (comment: RecommendationComment) => {
        setEditingComment(comment);
        setNewMessage(comment.message);
        setAnchorEl(null);
        setSelectedComment(null);
    };

    const handleDelete = async () => {
        if (!selectedComment) return;

        await deleteComment.trigger({
            id: selectedComment.id,
        });
        setAnchorEl(null);
        setSelectedComment(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedComment(null);
    };

    const onClose = () => {
        setCommentsDrawerOpen(false);
    };

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get('openComments') === 'true' && canReadComments) {
            setCommentsDrawerOpen(true);

            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openComments');
            setSearchParams(newParams, { replace: true });
        } else if (searchParams.get('openComments') === 'true' && !canReadComments) {
            // Close drawer if unauthorized user tries to open it
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openComments');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setCommentsDrawerOpen, setSearchParams, canReadComments]);

    // Close drawer if user doesn't have read permissions
    useEffect(() => {
        if (open && !canReadComments) {
            setCommentsDrawerOpen(false);
        }
    }, [open, canReadComments, setCommentsDrawerOpen]);

    // Don't render drawer if user doesn't have read permissions
    if (!canReadComments) {
        return null;
    }

    return (
        <Drawer
            anchor='right'
            open={open && canReadComments}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2,
                        borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant='h6' color='primary'>
                        {_('Comentarios')}
                    </Typography>
                    <IconButton onClick={onClose} size='small'>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Stack spacing={3} sx={{ overflow: 'scroll' }} p={3} pb={10}>
                    {/* Instructions */}
                    {canSendMessages && (
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {_(
                                'Dejá acá tus comentarios para que los miembros de tu squad los vean y trabajen la recomendación en conjunto.'
                            )}
                        </Typography>
                    )}

                    {/* Comments List */}
                    {isLoading ? (
                        <Typography variant='body2' color='primary.mui.inactive' sx={{ textAlign: 'center', py: 4 }}>
                            {_('Cargando comentarios...')}
                        </Typography>
                    ) : comments.length === 0 ? (
                        <Typography variant='body2' color='primary.mui.inactive' sx={{ textAlign: 'center', py: 4 }}>
                            {_('No hay comentarios aún. Sé el primero en comentar.')}
                        </Typography>
                    ) : (
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {comments.map((comment) => (
                                <RecommendationCommentItem
                                    key={comment.id}
                                    comment={comment}
                                    setAnchorEl={setAnchorEl}
                                    setSelectedComment={setSelectedComment}
                                    setEditingComment={setEditingComment}
                                    setNewMessage={setNewMessage}
                                    editingComment={editingComment}
                                    newMessage={newMessage}
                                    handleSend={handleSend}
                                    updateComment={updateComment}
                                    canSendMessages={canSendMessages}
                                />
                            ))}
                            <div ref={messagesEndRef} />
                        </List>
                    )}
                </Stack>

                {/* Input Area */}
                {!editingComment && canSendMessages && (
                    <Box
                        sx={{
                            p: 2,
                            borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                            bottom: '0',
                            position: 'absolute',
                            width: '100%',
                            background: 'white',
                        }}
                    >
                        <Stack spacing={1} direction='row' alignItems='center'>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder={_('Escribí un comentario...')}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                variant='outlined'
                                InputProps={{
                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleSend}
                                disabled={!newMessage.trim() || createComment.isMutating}
                                color='primary'
                                size='small'
                            >
                                <SendIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                )}
            </Box>

            {/* Menu for Edit/Delete */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => selectedComment && handleEdit(selectedComment)}>
                    <EditIcon sx={{ mr: 1, fontSize: '18px' }} />
                    {_('Editar')}
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <DeleteIcon sx={{ mr: 1, fontSize: '18px' }} color='error' />
                    {_('Eliminar')}
                </MenuItem>
            </Menu>
        </Drawer>
    );
};

export default RecommendationCommentsDrawer;
